import barcodeIcon from "@assets/images/barcode.svg";
import Button from "@material-ui/core/Button";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Filter from "@saleor/components/Filter";
import { IFilter } from "@saleor/components/Filter/types";
import OrderFilterTabs from "@saleor/components/OrderFilterTabs/OrderFilterTabs";
import { SearchBarProps } from "@saleor/components/SearchBar";
import SearchInput from "@saleor/components/SearchBar/SearchInput";
import { FilterTab } from "@saleor/components/TableFilter";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { FilterProps } from "../../../types";

export interface FilterBarProps<TKeys extends string = string>
  extends FilterProps<TKeys>,
    SearchBarProps {
  activeTab;
  filterStructure: IFilter<TKeys>;
  queryVariables;
  isAllAtStart?: boolean;
  openModal();
}

const useStyles = makeStyles(
  theme => ({
    cameraButton: {
      alignItems: "center",
      cursor: "pointer",
      paddingLeft: "5px",
      position: "absolute",
      right: "12px",
      top: "6px",
      zIndex: 9
    },
    cameraButtonImg: {
      width: "30px"
    },
    filterStatus: {
      "& .MuiTabs-scroller.MuiTabs-fixed": {
        overflowX: "scroll"
      }
    },
    root: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: "flex",
      flexWrap: "wrap",
      padding: theme.spacing(1, 3)
    },
    searchForm: {
      "& input[type=text]": {
        paddingRight: "52px"
      },
      display: "flex",
      flex: 1,
      position: "relative"
    },
    tabActionButton: {
      marginLeft: theme.spacing(2),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3)
    }
  }),
  {
    name: "FilterBar"
  }
);

const FilterBar: React.FC<FilterBarProps> = props => {
  const {
    openModal,
    activeTab,
    allTabLabel,
    currencySymbol,
    filterStructure,
    currentTab,
    initialSearch,
    searchPlaceholder,
    tabs,
    queryVariables,
    isAllAtStart,
    onAll,
    onSearchChange,
    onFilterChange,
    onTabChange,
    onTabDelete,
    onTabSave
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();
  const isSearchTermInLocalStorage =
    localStorage.getItem("orderFilters") !== null &&
    JSON.parse(localStorage.getItem("orderFilters")).filter(
      x => x.data.query === queryVariables.filter.search
    ).length > 0;

  const DEFAULT_TABS_LAST_INDEX = 4;

  const isCustomFilter =
    activeTab !== undefined &&
    activeTab > DEFAULT_TABS_LAST_INDEX &&
    isSearchTermInLocalStorage;

  const hasSelectedDefaultTab = queryVariables.filter.smopStatus !== null;

  const hasSearchTerms =
    queryVariables.filter.search !== undefined &&
    queryVariables.filter.search !== "";

  const showCustomFilterTab =
    queryVariables.filter.smopStatus === null &&
    queryVariables.filter.search !== undefined &&
    queryVariables.filter.search !== "" &&
    isSearchTermInLocalStorage === false;

  const displayTabAction = hasSelectedDefaultTab
    ? null
    : isCustomFilter
    ? "delete"
    : "save";

  return (
    <>
      <div className={classes.filterStatus}>
        <OrderFilterTabs currentTab={currentTab}>
          {isAllAtStart ? (
            <FilterTab label={allTabLabel} onClick={onAll} />
          ) : null}
          {tabs.map((tab, tabIndex) => (
            <FilterTab
              onClick={() =>
                onTabChange(isAllAtStart ? tabIndex + 1 : tabIndex)
              }
              label={tab}
              key={tabIndex}
            />
          ))}
          {!isAllAtStart ? (
            <FilterTab label={allTabLabel} onClick={onAll} />
          ) : null}
          {showCustomFilterTab && (
            <FilterTab
              onClick={() => undefined}
              label={intl.formatMessage({
                defaultMessage: "Custom Filter"
              })}
            />
          )}
        </OrderFilterTabs>
      </div>
      <div className={classes.root}>
        <Filter
          currencySymbol={currencySymbol}
          menu={filterStructure}
          onFilterAdd={onFilterChange}
        />
        <div className={classes.searchForm}>
          <SearchInput
            initialSearch={initialSearch}
            placeholder={searchPlaceholder}
            onSearchChange={onSearchChange}
          />
          <div className={classes.cameraButton} onClick={openModal}>
            <img
              src={barcodeIcon}
              alt="Barcode Icon"
              className={classes.cameraButtonImg}
            />
          </div>
        </div>
        {displayTabAction &&
          (displayTabAction === "save" && hasSearchTerms ? (
            <Button
              className={classes.tabActionButton}
              color="primary"
              onClick={onTabSave}
            >
              <FormattedMessage
                defaultMessage="Save Search"
                description="button"
              />
            </Button>
          ) : (
            displayTabAction === "delete" && (
              <Button
                className={classes.tabActionButton}
                color="primary"
                onClick={onTabDelete}
              >
                <FormattedMessage
                  defaultMessage="Delete Search"
                  description="button"
                />
              </Button>
            )
          ))}
      </div>
    </>
  );
};
FilterBar.displayName = "FilterBar";
export default FilterBar;
